import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { Ellipsis, Loader2, MessageSquareText, PackageMinus, PackagePlus, Pencil, Send, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  deleteOwnedDirectorSkill,
  directorSkillPublicSlug,
  publishOwnedDirectorSkill,
  readOwnedDirectorSkill,
  resolveRuntimeDirectorSkill,
  skillVersionResultToDirectorSkillSummary,
  type DirectorSkillSummary,
} from '../../api/directorSkills'
import type { SkillVersionResult, SkillView } from '../../generated/directorSkillTypes'
import { useAuth } from '../../contexts/AuthContext'
import { createNadDebug } from '../../utils/debugLog'
import { SkillCoverMedia } from './SkillCoverMedia'
import { SkillEditor } from './SkillEditor'
import { trackSkillLibraryAction } from '../../analytics/chatHeaderAnalytics'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'

const detailLog = createNadDebug('dw:director-skills:detail')

interface SkillDetailProps {
  skill: DirectorSkillSummary
  onBack: () => void
  onUse: (skill: DirectorSkillSummary) => void
  onEditWithDirector: (skill: DirectorSkillSummary) => void
  onUpdated: (skill: DirectorSkillSummary) => void
  onDeleted: (skillId: string) => void
  onInstall: (skill: DirectorSkillSummary) => Promise<void>
  onUpdate: (skill: DirectorSkillSummary) => Promise<void>
  onUninstall: (skill: DirectorSkillSummary) => Promise<void>
  useButtonRef?: RefObject<HTMLButtonElement>
}

function DetailRow({ label, children, testId }: { label: string; children: string; testId: string }) {
  return (
    <div className="grid gap-1 py-2.5 sm:grid-cols-[120px_1fr]" data-testid={testId}>
      <dt className="text-sm text-text-muted">{label}</dt>
      <dd className="text-base leading-relaxed text-foreground">{children}</dd>
    </div>
  )
}

export function SkillDetail({
  skill,
  onBack,
  onUse,
  onEditWithDirector,
  onUpdated,
  onDeleted,
  onInstall,
  onUpdate,
  onUninstall,
  useButtonRef,
}: SkillDetailProps) {
  const { t } = useTranslation('chat')
  const { user } = useAuth()
  const [ownerDetail, setOwnerDetail] = useState<SkillView | null>(null)
  const [editing, setEditing] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [busyAction, setBusyAction] = useState<'publish' | 'delete' | 'install' | 'update' | 'uninstall' | null>(null)
  const [publicationStatus, setPublicationStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const loadOwnerDetail = useCallback(async () => {
    if (skill.source !== 'user') {
      setOwnerDetail(null)
      return
    }
    setError(null)
    try {
      const next = await readOwnedDirectorSkill(skill.skillId)
      setOwnerDetail(next)
      detailLog({
        event: 'skill-detail:owner-loaded',
        trace_id: `director-skills-detail:${skill.skillId}`,
        skill_id: skill.skillId,
        version: next.version ?? skill.version,
        file_count: next.files?.length ?? 0,
        cover_kind: next.cover?.kind ?? 'none',
        outcome: 'success',
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    }
  }, [skill.skillId, skill.source, skill.version])

  useEffect(() => {
    void loadOwnerDetail()
  }, [loadOwnerDetail])

  useEffect(() => {
    if (!menuOpen) return
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menuOpen])

  const display = ownerDetail
    ? {
        displayName: ownerDetail.display_name ?? skill.displayName,
        description: ownerDetail.description ?? skill.description,
        useWhen: ownerDetail.use_when ?? skill.useWhen,
        howToUse: ownerDetail.how_to_use ?? skill.howToUse,
        outputContent: ownerDetail.output_content ?? skill.outputContent,
        cover: ownerDetail.cover ?? skill.cover,
        version: ownerDetail.version ?? skill.version,
      }
    : skill
  const analyticsProjectId = useOptionalProjectId()
  const runtimeSkill = resolveRuntimeDirectorSkill(skill)
  const runtimeStatus =
    skill.source === 'published' && skill.available === false
      ? 'unavailable'
      : skill.enabled === false
        ? 'disabled'
        : skill.available === false
          ? 'unavailable'
          : null
  const updateAvailable = skill.source === 'published' && skill.installed === true && skill.updateAvailable === true

  const handleSaved = useCallback(
    (result: SkillVersionResult) => {
      const summary = skillVersionResultToDirectorSkillSummary(result)
      setEditing(false)
      onUpdated(summary)
      setOwnerDetail((current) =>
        current
          ? {
              ...current,
              ref: result.ref,
              version_id: result.version_id,
              version: result.version,
              name: result.name,
              display_name: result.display_name,
              description: result.description,
              use_when: result.use_when,
              how_to_use: result.how_to_use,
              output_content: result.output_content,
              cover: result.cover,
              enabled: result.enabled,
              is_active: result.is_active,
            }
          : current,
      )
      void loadOwnerDetail()
    },
    [loadOwnerDetail, onUpdated],
  )

  const publish = useCallback(async () => {
    const versionId = ownerDetail?.version_id ?? skill.versionId
    setBusyAction('publish')
    setError(null)
    try {
      const profileName = user?.user_metadata?.full_name
      const publisherName =
        typeof profileName === 'string' && profileName.trim()
          ? profileName.trim().slice(0, 80)
          : (user?.email?.split('@')[0] || 'Dreamwrite creator').slice(0, 80)
      const publication = await publishOwnedDirectorSkill(skill.skillId, {
        version_id: versionId,
        publisher_display_name: publisherName,
        category: 'general',
        tags: [],
        change_summary: `Publish ${display.displayName} version ${display.version}.`,
      })
      setPublicationStatus(publication.status)
      detailLog({
        event: 'skill-detail:publication-submitted',
        trace_id: `director-skills-detail:${skill.skillId}`,
        skill_id: skill.skillId,
        version_id: versionId,
        status: publication.status,
        outcome: 'success',
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setBusyAction(null)
    }
  }, [display.displayName, display.version, ownerDetail?.version_id, skill.skillId, skill.versionId, user])

  const remove = useCallback(async () => {
    setBusyAction('delete')
    setError(null)
    try {
      await deleteOwnedDirectorSkill(skill.skillId)
      detailLog({
        event: 'skill-detail:deleted',
        trace_id: `director-skills-detail:${skill.skillId}`,
        skill_id: skill.skillId,
        outcome: 'success',
      })
      onDeleted(skill.skillId)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
      setBusyAction(null)
    }
  }, [onDeleted, skill.skillId])

  const changeInstallation = useCallback(
    async (action: 'install' | 'update' | 'uninstall') => {
      setBusyAction(action)
      setError(null)
      try {
        if (action === 'install') await onInstall(skill)
        else if (action === 'update') await onUpdate(skill)
        else await onUninstall(skill)
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : String(reason))
      } finally {
        setBusyAction(null)
      }
    },
    [onInstall, onUninstall, onUpdate, skill],
  )

  if (editing && ownerDetail) {
    return <SkillEditor skill={skill} detail={ownerDetail} onCancel={() => setEditing(false)} onSaved={handleSaved} />
  }

  return (
    <div className="space-y-5" data-testid="director-skill-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-text-muted">
          <span>/{directorSkillPublicSlug(skill)}</span>
          <span aria-hidden="true">·</span>
          <span>{t('skillPicker.version', { version: display.version })}</span>
          <span className="rounded-full border border-border px-2 py-0.5">
            {skill.source === 'builtin'
              ? t('skillPicker.builtin')
              : skill.source === 'published'
                ? skill.installed
                  ? t('skillPicker.installed')
                  : t('skillPicker.discover')
                : t('skillPicker.mine')}
          </span>
          {skill.source === 'published' && skill.installed && skill.installedVersion !== undefined && skill.installedVersion !== display.version && (
            <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-primary" data-testid="director-skill-pinned-version">
              {t('skillPicker.installedVersion', {
                version: skill.installedVersion,
              })}
            </span>
          )}
          {runtimeStatus && (
            <span
              className="rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-destructive"
              data-testid="director-skill-runtime-unavailable"
            >
              {t(runtimeStatus === 'unavailable' ? 'skillPicker.unavailable' : 'skillPicker.disabled')}
            </span>
          )}
          {skill.publisherDisplayName && (
            <span>
              {t('skillPicker.byPublisher', {
                publisher: skill.publisherDisplayName,
              })}
            </span>
          )}
          {(publicationStatus ?? ownerDetail?.publication_status) && (
            <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-primary" data-testid="skill-publication-status">
              {publicationStatus ?? ownerDetail?.publication_status}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {skill.source === 'user' && (
            <>
              <div ref={menuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((value) => !value)}
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                  aria-label={t('skillPicker.editOptions')}
                  className="flex h-9 w-10 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                  data-testid="skill-owner-menu"
                >
                  <Ellipsis className="h-4 w-4" />
                </button>
                {menuOpen && (
                  <div role="menu" className="absolute right-0 top-11 z-20 w-52 rounded-lg border border-border bg-background p-1 shadow-xl">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false)
                        setEditing(true)
                      }}
                      disabled={!ownerDetail}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
                      data-testid="skill-owner-edit"
                    >
                      <Pencil className="h-4 w-4" /> {t('skillPicker.edit')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        if (!runtimeSkill) return
                        setMenuOpen(false)
                        onEditWithDirector(runtimeSkill)
                      }}
                      disabled={!runtimeSkill}
                      aria-describedby={!runtimeSkill ? 'skill-director-edit-status' : undefined}
                      title={!runtimeSkill ? t('skillPicker.editWithDirectorUnavailable') : undefined}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:text-text-muted disabled:opacity-60 disabled:hover:bg-transparent"
                      data-testid="skill-owner-edit-director"
                    >
                      <MessageSquareText className="h-4 w-4" /> {t('skillPicker.editWithDirector')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false)
                        setConfirmDelete(true)
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
                      data-testid="skill-owner-delete"
                    >
                      <Trash2 className="h-4 w-4" /> {t('skillPicker.delete')}
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { trackSkillLibraryAction(analyticsProjectId, 'publish', skill); void publish() }}
                disabled={busyAction !== null || publicationStatus === 'pending' || ownerDetail?.publication_status === 'pending'}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-input-border bg-surface-1 px-4 text-sm text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
                data-testid="skill-owner-publish"
              >
                {busyAction === 'publish' && <Loader2 className="h-4 w-4 animate-spin" />} {t('skillPicker.publish')}
              </button>
            </>
          )}
          {skill.source === 'published' && !skill.installed && (
            <button
              ref={useButtonRef}
              type="button"
              onClick={() => { trackSkillLibraryAction(analyticsProjectId, 'install', skill); void changeInstallation('install') }}
              disabled={busyAction !== null}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
              data-testid="director-skill-install"
            >
              {busyAction === 'install' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackagePlus className="h-4 w-4" />}
              {t('skillPicker.addSkill')}
            </button>
          )}
          {skill.source === 'published' && skill.installed && (
            <>
              {updateAvailable && (
                <button
                  type="button"
                  onClick={() => { trackSkillLibraryAction(analyticsProjectId, 'update', skill); void changeInstallation('update') }}
                  disabled={busyAction !== null}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
                  data-testid="director-skill-update"
                >
                  {busyAction === 'update' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackagePlus className="h-4 w-4" />}
                  {t('skillPicker.updateSkill')}
                </button>
              )}
              <button
                type="button"
                onClick={() => { trackSkillLibraryAction(analyticsProjectId, 'uninstall', skill); void changeInstallation('uninstall') }}
                disabled={busyAction !== null}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-input-border bg-surface-1 px-4 text-sm text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
                data-testid="director-skill-uninstall"
              >
                {busyAction === 'uninstall' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageMinus className="h-4 w-4" />}
                {t('skillPicker.removeSkill')}
              </button>
            </>
          )}
          {runtimeSkill && (
            <button
              ref={useButtonRef}
              type="button"
              onClick={() => onUse(runtimeSkill)}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              data-testid="director-skill-detail-use"
            >
              <Send className="h-4 w-4" /> {t('skillPicker.useSkill')}
            </button>
          )}
        </div>
      </div>

      {display.cover && <SkillCoverMedia cover={display.cover} title={display.displayName} mode="detail" />}

      <dl className="divide-y divide-border-subtle" data-testid="skill-detail-metadata">
        <DetailRow label={t('skillPicker.introduction')} testId="director-skill-detail-introduction">
          {display.description}
        </DetailRow>
        <DetailRow label={t('skillPicker.usageScenario')} testId="director-skill-detail-usage-scenario">
          {display.useWhen}
        </DetailRow>
        <DetailRow label={t('skillPicker.howToUse')} testId="director-skill-detail-how-to-use">
          {display.howToUse}
        </DetailRow>
        <DetailRow label={t('skillPicker.outputContent')} testId="director-skill-detail-output-content">
          {display.outputContent}
        </DetailRow>
      </dl>

      {skill.source === 'user' && (
        <p
          id="skill-director-edit-status"
          className={`rounded-lg border px-3 py-2 text-xs font-medium leading-relaxed ${runtimeSkill ? 'border-border bg-surface-0 text-muted-foreground' : 'border-destructive/40 bg-destructive/10 text-destructive'}`}
          data-testid={runtimeSkill ? 'skill-director-edit-capabilities' : 'skill-director-edit-unavailable'}
        >
          {t(runtimeSkill ? 'skillPicker.editWithDirectorCapabilities' : 'skillPicker.editWithDirectorUnavailable')}
        </p>
      )}

      {confirmDelete && (
        <div
          role="alertdialog"
          aria-label={t('skillPicker.delete')}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3"
        >
          <p className="text-sm text-foreground">{t('skillPicker.deleteQuestion')}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="h-8 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3"
            >
              {t('skillPicker.cancel')}
            </button>
            <button
              type="button"
              onClick={() => { trackSkillLibraryAction(analyticsProjectId, 'delete', skill); void remove() }}
              disabled={busyAction === 'delete'}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-destructive px-3 text-xs font-medium text-white hover:bg-destructive/90 disabled:opacity-50"
              data-testid="skill-owner-confirm-delete"
            >
              {busyAction === 'delete' && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {t('skillPicker.delete')}
            </button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="border-t border-border-subtle pt-4">
        <button
          type="button"
          onClick={onBack}
          className="h-9 rounded-lg border border-input-border bg-surface-1 px-4 text-sm text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          {t('skillPicker.back')}
        </button>
      </div>
    </div>
  )
}
